import React, { useState, useEffect } from 'react';
import { X, MessageCircle, Plus, CreditCard as Edit, Trash2, Save, ArrowLeft, Settings } from 'lucide-react';
import { WhatsAppTemplate } from '../../types/crm';
import { supabaseWhatsAppTemplateStorage } from '../../services/supabaseStorage';
import { useToast } from '../../contexts/ToastContext';
import { PlaceholderService } from '../../services/placeholderService';

interface ManageWhatsAppTemplatesModalProps {
  onClose: () => void;
  onTemplatesUpdated: () => void;
}

const AVAILABLE_PLACEHOLDERS = [
  '{{ContactFirstName}}',
  '{{ContactLastName}}',
  '{{ContactEmail}}',
  '{{ContactPhoneNumber}}',
  '{{CompanyName}}',
  '{{CourseName}}',
  '{{CourseLanguage}}',
  '{{CourseDate}}',
  '{{StartTime}}',
  '{{EndTime}}',
  '{{Location}}',
  '{{AttendeeCount}}',
  '{{CourseFee}}',
  '{{AmountPaid}}',
  '{{BalanceDue}}',
  '{{PaymentStatus}}',
  '{{Notes}}',
  '{{AllBookingDates}}',
  '{{NextUpcomingDate}}',
  '{{TimeBasedGreeting}}',
  '{{CourseSessionReference}}',
];

export function ManageWhatsAppTemplatesModal({ onClose, onTemplatesUpdated }: ManageWhatsAppTemplatesModalProps) {
  const [templates, setTemplates] = useState<WhatsAppTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editingTemplate, setEditingTemplate] = useState<WhatsAppTemplate | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({ name: '', body: '', isDefault: false });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const { showSuccess, showError } = useToast();

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      setIsLoading(true);
      const loaded = await supabaseWhatsAppTemplateStorage.getWhatsAppTemplates();
      setTemplates(loaded);
    } catch (error) {
      showError('Failed to load WhatsApp templates');
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const validateForm = () => {
    const next: Record<string, string> = {};
    if (!formData.name.trim()) next.name = 'Template name is required';
    if (!formData.body.trim()) next.body = 'Message body is required';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    try {
      const template: WhatsAppTemplate = {
        id: editingTemplate?.id || crypto.randomUUID(),
        name: formData.name.trim(),
        body: formData.body.trim(),
        isDefault: formData.isDefault,
      };

      if (editingTemplate) {
        await supabaseWhatsAppTemplateStorage.updateWhatsAppTemplate(template);
        setTemplates((prev) => prev.map((t) => (t.id === template.id ? template : t)));
        showSuccess('Template updated');
      } else {
        await supabaseWhatsAppTemplateStorage.addWhatsAppTemplate(template);
        setTemplates((prev) => [...prev, template]);
        showSuccess('Template created');
      }

      resetForm();
      onTemplatesUpdated();
    } catch (error) {
      showError('Failed to save template');
      console.error(error);
    }
  };

  const handleEdit = (template: WhatsAppTemplate) => {
    setEditingTemplate(template);
    setFormData({
      name: template.name,
      body: template.body,
      isDefault: template.isDefault,
    });
    setShowAddForm(true);
  };

  const handleDelete = async (template: WhatsAppTemplate) => {
    if (!window.confirm(`Delete WhatsApp template "${template.name}"?`)) return;
    try {
      await supabaseWhatsAppTemplateStorage.deleteWhatsAppTemplate(template.id);
      setTemplates((prev) => prev.filter((t) => t.id !== template.id));
      showSuccess('Template deleted');
      onTemplatesUpdated();
    } catch (error) {
      showError('Failed to delete template');
      console.error(error);
    }
  };

  const resetForm = () => {
    setFormData({ name: '', body: '', isDefault: false });
    setEditingTemplate(null);
    setShowAddForm(false);
    setErrors({});
  };

  const insertPlaceholder = (ph: string) => {
    const ta = document.getElementById('whatsappBodyEditor') as HTMLTextAreaElement | null;
    if (ta) {
      const start = ta.selectionStart;
      const end = ta.selectionEnd;
      const text = formData.body;
      const next = text.slice(0, start) + ph + text.slice(end);
      setFormData((p) => ({ ...p, body: next }));
      setTimeout(() => {
        ta.focus();
        ta.setSelectionRange(start + ph.length, start + ph.length);
      }, 0);
    } else {
      setFormData((p) => ({ ...p, body: p.body + ph }));
    }
  };

  const previewSample = (body: string) => {
    if (!body.trim()) return '…';
    const sampleContext = {
      contact: { firstName: 'John', lastName: 'Smith', email: 'john@example.com', phoneNumber: '087 123 4567' },
      bookingData: {
        courseName: 'Forklift Training',
        courseLanguage: 'English',
        courseDate: '2026-05-15',
        startTime: '09:00',
        endTime: '17:00',
        attendeeCount: 8,
        notes: 'Bring PPE',
        allBookingDates: ['2026-05-15'],
      },
      location: 'Training Centre, Dublin',
      customerName: 'Sample Company Ltd',
      companyName: 'Sample Company Ltd',
    };
    const placeholders = PlaceholderService.generatePlaceholders(sampleContext as any);
    placeholders['{{CourseFee}}'] = '€120.00';
    placeholders['{{AmountPaid}}'] = '€60.00';
    placeholders['{{BalanceDue}}'] = '€60.00';
    placeholders['{{PaymentStatus}}'] = 'half_paid';
    return PlaceholderService.replacePlaceholders(body, placeholders as any);
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-start justify-center overflow-y-auto bg-black/50 p-4 pt-16 backdrop-blur-sm">
      <div className="max-h-[85vh] w-full max-w-4xl overflow-y-auto rounded-3xl border border-white/20 bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-200 p-6">
          <button
            type="button"
            onClick={onClose}
            className="group mr-4 inline-flex items-center rounded-lg px-3 py-2 text-sm font-medium text-slate-600 transition-all hover:bg-slate-100 hover:text-slate-800"
          >
            <ArrowLeft className="mr-1 h-4 w-4 transition-transform group-hover:-translate-x-1" />
            Back
          </button>
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600">
              <Settings className="h-5 w-5 text-white" />
            </div>
            <h2 className="text-xl font-bold text-slate-800">WhatsApp Templates</h2>
          </div>
          <button type="button" onClick={onClose} className="rounded-xl p-2 transition-colors hover:bg-slate-100">
            <X className="h-5 w-5 text-slate-500" />
          </button>
        </div>

        <div className="space-y-6 p-6">
          <p className="text-sm text-slate-600">
            Plain-text messages for WhatsApp (desktop/app link). Use the same <code className="rounded bg-slate-100 px-1 text-xs">{'{{Placeholders}}'}</code> as email
            templates where applicable — they are filled when you contact an attendee from course sessions.
          </p>

          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-slate-800">Templates</h3>
            <button
              type="button"
              onClick={() => {
                resetForm();
                setShowAddForm(true);
              }}
              className="inline-flex items-center rounded-xl bg-gradient-to-r from-emerald-500 to-teal-600 px-4 py-2 font-medium text-white transition hover:scale-105 hover:shadow-lg"
            >
              <Plus className="mr-2 h-4 w-4" />
              Add template
            </button>
          </div>

          {showAddForm && (
            <div className="rounded-2xl border border-slate-200 bg-slate-50/80 p-5">
              <h4 className="mb-4 font-semibold text-slate-800">{editingTemplate ? 'Edit template' : 'New template'}</h4>
              <div className="space-y-4">
                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700">Name</label>
                  <input
                    value={formData.name}
                    onChange={(e) => {
                      setFormData((p) => ({ ...p, name: e.target.value }));
                      if (errors.name) setErrors((er) => ({ ...er, name: '' }));
                    }}
                    className={`w-full rounded-xl border px-4 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 ${
                      errors.name ? 'border-red-300 bg-red-50' : 'border-slate-200'
                    }`}
                    placeholder="e.g. Session reminder"
                  />
                  {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700">Insert placeholder</label>
                  <select
                    className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                    defaultValue=""
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v) insertPlaceholder(v);
                      e.target.value = '';
                    }}
                  >
                    <option value="">Choose placeholder…</option>
                    {AVAILABLE_PLACEHOLDERS.map((ph) => (
                      <option key={ph} value={ph}>
                        {ph}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700">Message body</label>
                  <textarea
                    id="whatsappBodyEditor"
                    value={formData.body}
                    onChange={(e) => {
                      setFormData((p) => ({ ...p, body: e.target.value }));
                      if (errors.body) setErrors((er) => ({ ...er, body: '' }));
                    }}
                    rows={14}
                    className={`w-full resize-none rounded-xl border px-4 py-3 font-mono text-sm leading-relaxed focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 ${
                      errors.body ? 'border-red-300 bg-red-50' : 'border-slate-200'
                    }`}
                    placeholder={`Hi {{ContactFirstName}},\n\nReminder: {{CourseName}} on {{CourseDate}} at {{Location}}.`}
                  />
                  {errors.body && <p className="mt-1 text-sm text-red-600">{errors.body}</p>}
                </div>

                <div className="rounded-xl border border-slate-200 bg-white p-3">
                  <p className="mb-2 text-xs font-medium text-slate-500">Preview (sample data)</p>
                  <pre className="max-h-40 overflow-auto whitespace-pre-wrap text-xs text-slate-700">{previewSample(formData.body)}</pre>
                </div>

                <label className="flex items-center gap-2 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    checked={formData.isDefault}
                    onChange={(e) => setFormData((p) => ({ ...p, isDefault: e.target.checked }))}
                    className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                  />
                  Use as default when opening WhatsApp from sessions
                </label>

                <div className="flex justify-end gap-3 pt-2">
                  <button type="button" onClick={resetForm} className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleSave}
                    className="inline-flex items-center rounded-xl bg-gradient-to-r from-emerald-500 to-teal-600 px-4 py-2 text-sm font-medium text-white hover:shadow-lg"
                  >
                    <Save className="mr-2 h-4 w-4" />
                    {editingTemplate ? 'Update' : 'Save'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {!showAddForm && (
            <div>
              {isLoading ? (
                <div className="py-12 text-center text-slate-500">Loading…</div>
              ) : templates.length === 0 ? (
                <div className="py-12 text-center">
                  <MessageCircle className="mx-auto mb-4 h-12 w-12 text-slate-300" />
                  <p className="text-slate-600">No WhatsApp templates yet. Add one — until then, session WhatsApp uses the built-in default text.</p>
                </div>
              ) : (
                <div className="grid gap-3">
                  {templates.map((t) => (
                    <div key={t.id} className="flex items-start justify-between rounded-xl border border-slate-200 p-4">
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <h4 className="font-semibold text-slate-800">{t.name}</h4>
                          {t.isDefault && (
                            <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-800">Default</span>
                          )}
                        </div>
                        <p className="mt-1 line-clamp-2 text-xs text-slate-500">{t.body}</p>
                      </div>
                      <div className="ml-3 flex shrink-0 gap-1">
                        <button type="button" onClick={() => handleEdit(t)} className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-800">
                          <Edit className="h-4 w-4" />
                        </button>
                        <button type="button" onClick={() => handleDelete(t)} className="rounded-lg p-2 text-red-500 hover:bg-red-50">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex justify-between border-t border-slate-200 p-6">
          <button type="button" onClick={onClose} className="rounded-xl border border-slate-300 px-6 py-3 font-medium text-slate-700 hover:bg-slate-50">
            Close
          </button>
          <button
            type="button"
            onClick={() => {
              onTemplatesUpdated();
              onClose();
            }}
            className="rounded-xl bg-gradient-to-r from-emerald-500 to-teal-600 px-6 py-3 font-medium text-white hover:shadow-lg"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
